import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { Bot, Send, Trash2, Sparkles, X, GripVertical } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage key for position
const POSITION_KEY = "moodbot_position";

// Get saved position or default
const getSavedPosition = () => {
  try {
    const saved = localStorage.getItem(POSITION_KEY);
    if (saved) {
      const pos = JSON.parse(saved);
      // Validate position is within viewport
      const maxX = window.innerWidth - 60;
      const maxY = window.innerHeight - 60;
      return {
        x: Math.min(Math.max(0, pos.x), maxX),
        y: Math.min(Math.max(0, pos.y), maxY)
      };
    }
  } catch (e) {
    console.error("Error loading position:", e);
  }
  // Default position: bottom right
  return { x: window.innerWidth - 80, y: window.innerHeight - 80 };
};

export const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "¡Hola! Soy MoodBot, tu asistente de bienestar emocional. ¿Cómo te sientes hoy? Estoy aquí para escucharte.",
      suggestions: ["Me siento bien", "Necesito hablar", "¿Qué puedes hacer?"]
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Dragging state
  const [position, setPosition] = useState(getSavedPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false); // Track if mouse actually moved
  const dragRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const buttonStartPos = useRef({ x: 0, y: 0 });

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - 60),
        y: Math.min(prev.y, window.innerHeight - 60)
      }));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Save position to localStorage
  const savePosition = useCallback((pos) => {
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
    } catch (e) {
      console.error("Error saving position:", e);
    }
  }, []);

  // Mouse/Touch handlers for dragging
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setHasMoved(false); // Reset movement tracking
    
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    
    dragStartPos.current = { x: clientX, y: clientY };
    buttonStartPos.current = { ...position };
  }, [position]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - dragStartPos.current.x;
    const deltaY = clientY - dragStartPos.current.y;
    
    // Only consider it a move if moved more than 5 pixels
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setHasMoved(true);
    }
    
    const newX = Math.min(Math.max(0, buttonStartPos.current.x + deltaX), window.innerWidth - 60);
    const newY = Math.min(Math.max(0, buttonStartPos.current.y + deltaY), window.innerHeight - 60);
    
    setPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      // If user didn't actually move, treat as click
      if (!hasMoved) {
        setIsOpen(true);
      } else {
        savePosition(position);
      }
      setIsDragging(false);
      setHasMoved(false);
    }
  }, [isDragging, hasMoved, position, savePosition]);

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (text) => {
    const messageText = text || input.trim();
    if (!messageText || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: messageText }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot`, {
        message: messageText
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response,
        suggestions: response.data.suggestions || []
      }]);
    } catch (error) {
      console.error("Chatbot error:", error);
      toast.error("Error al conectar con MoodBot");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Lo siento, tuve un problema al procesar tu mensaje. ¿Puedes intentarlo de nuevo?",
        suggestions: []
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/chatbot/history`);
      setMessages([{
        role: "assistant",
        content: "¡Conversación reiniciada! ¿En qué puedo ayudarte hoy?",
        suggestions: ["Me siento bien", "Necesito hablar", "Dame un consejo"]
      }]);
      toast.success("Historial limpiado");
    } catch (error) {
      toast.error("Error al limpiar historial");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Draggable Button - Always visible */}
      <div
        ref={dragRef}
        className={`fixed z-40 touch-none ${isOpen ? 'hidden' : ''}`}
        style={{
          left: position.x,
          top: position.y,
          cursor: isDragging ? "grabbing" : "grab"
        }}
      >
        <div
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          className={`relative p-4 rounded-full bg-gradient-to-r from-neon-purple to-neon-cyan shadow-lg hover:shadow-neon-purple/50 transition-all ${isDragging ? 'scale-110' : 'hover:scale-105'}`}
          data-testid="chatbot-toggle"
        >
          <Bot className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-neon-green rounded-full animate-pulse" />
          
          {/* Drag indicator */}
          <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-white/20 rounded-full flex items-center justify-center">
            <GripVertical className="w-3 h-3 text-white/70" />
          </div>
        </div>
      </div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)]"
          >
            <GlassCard className="flex flex-col h-full overflow-hidden" neon>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-neon-purple/20 border border-neon-purple/30">
                    <Bot className="w-5 h-5 text-neon-purple" />
                  </div>
                  <div>
                    <h3 className="font-orbitron text-sm text-white">MoodBot</h3>
                    <p className="text-xs text-slate-400 font-rajdhani">Tu asistente emocional</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearHistory}
                    className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-neon-red transition-colors"
                    title="Limpiar historial"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, index) => (
                  <motion.div
                    key={`msg-${index}-${msg.role}-${msg.content.substring(0, 20)}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`max-w-[85%] ${msg.role === "user" ? "order-1" : ""}`}>
                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          msg.role === "user"
                            ? "bg-neon-cyan/20 border border-neon-cyan/30 text-white"
                            : "bg-white/5 border border-white/10 text-slate-300"
                        }`}
                      >
                        <p className="text-sm font-inter">{msg.content}</p>
                      </div>
                      
                      {/* Suggestions */}
                      {msg.role === "assistant" && msg.suggestions?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {msg.suggestions.map((suggestion, idx) => (
                            <button
                              key={`suggestion-${index}-${idx}-${suggestion.substring(0, 10)}`}
                              onClick={() => sendMessage(suggestion)}
                              disabled={loading}
                              className="text-xs px-3 py-1 rounded-full bg-neon-purple/10 border border-neon-purple/30 text-neon-purple hover:bg-neon-purple/20 transition-colors disabled:opacity-50"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
                
                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2 text-slate-400"
                  >
                    <Sparkles className="w-4 h-4 animate-pulse text-neon-purple" />
                    <span className="text-sm font-rajdhani">MoodBot está escribiendo...</span>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Escribe un mensaje..."
                    disabled={loading}
                    className="flex-1 bg-black/30 border-white/10 text-white placeholder:text-slate-500"
                    data-testid="chatbot-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    className="cyber-button-cyan px-3"
                    data-testid="chatbot-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
